import api from './api';
import type { Quote, QuoteCreateRequest, QuoteFilters, PaginatedResponse, InsurerQuote } from '@/types';

/** An attachment on a quote request. Mirrors apps.quotes.models.QuoteDocument. */
export interface QuoteDocument {
  id: string;
  quote: string | null;
  quote_request: string | null;
  document_type: string;
  document_type_display: string;
  name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string;
  is_verified: boolean;
  created_at: string;
}

/**
 * One priced insurer option on a quote request — `QuoteListSerializer`.
 *
 * Only the fields the read-only screens render are declared; the serializer
 * sends more (premium_breakdown, addons_available, idv, documents). `documents`
 * is deliberately absent: `QuoteDocumentViewSet.get_queryset` scopes by broker,
 * so a customer login resolves to no broker and every attachment 404s — a
 * download link there would be a dead button.
 */
export interface QuoteOption {
  id: string;
  quote_number: string;
  insurer: string | null;
  insurer_name: string | null;
  insurer_logo: string | null;
  product_name: string | null;
  total_premium: string | number | null;
  net_premium: string | number | null;
  tax_amount: number | null;
  cover_start: string | null;
  cover_end: string | null;
  status: string;
  status_display: string | null;
  is_recommended: boolean;
  valid_until: string | null;
  issued_policy_number: string | null;
}

/** One {id, name} option, as `QuoteRequestViewSet.filter_options` returns
 *  each of its two lists (item C7). */
export interface QuoteFilterOption {
  id: string;
  name: string;
}

export interface QuoteFilterOptions {
  insurance_types: QuoteFilterOption[];
  insurers: QuoteFilterOption[];
}

export const quoteService = {
  async getQuotes(
    page: number = 1,
    filters?: QuoteFilters
  ): Promise<PaginatedResponse<Quote>> {
    const params = new URLSearchParams();
    params.append('page', page.toString());

    if (filters?.customer) params.append('customer', filters.customer);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.business_type) params.append('business_type', filters.business_type);
    if (filters?.insurance_type) params.append('insurance_type', filters.insurance_type);
    // Item C7: `insurer` and the date-range pair are ad-hoc params
    // `QuoteRequestViewSet.get_queryset` reads directly (no `filterset_fields`
    // entry for any of the three -- see that method's comments for why).
    if (filters?.insurer) params.append('insurer', filters.insurer);
    if (filters?.created_from) params.append('created_from', filters.created_from);
    if (filters?.created_to) params.append('created_to', filters.created_to);

    const response = await api.get<PaginatedResponse<Quote>>(`/quotes/requests/?${params.toString()}`);
    return response.data;
  },

  /**
   * Item C7: distinct Insurance Type / Insurance Company values PRESENT on
   * the caller's own scoped quote requests (`apps/quotes/views.py`
   * `QuoteRequestViewSet.filter_options`, built off the same `get_queryset()`
   * the list itself uses).
   */
  async getFilterOptions(): Promise<QuoteFilterOptions> {
    const response = await api.get<QuoteFilterOptions>('/quotes/requests/filter-options/');
    return response.data;
  },

  // Wave F1 item 4a: the quote-compare screen's REST replacement for the
  // broken socket.io stream (see frontend/src/hooks/useQuoteStream.ts's
  // former usage). Quotes are priced synchronously during the submit POST
  // (backend/apps/integrations/services/quote_service.py), so by the time
  // the wizard navigates here the rows already exist — this just reads them
  // back via the existing broker-scoped QuoteViewSet list endpoint.
  async getInsurerQuotesForRequest(quoteRequestId: string): Promise<InsurerQuote[]> {
    const response = await api.get<PaginatedResponse<InsurerQuote>>(
      `/quotes/quotes/?quote_request=${quoteRequestId}`
    );
    return response.data.results;
  },

  /**
   * The priced insurer options on one quote request, as the LIST serializer
   * actually sends them.
   *
   * `getInsurerQuotesForRequest` above hits the same URL but types the rows as
   * `InsurerQuote`, the ADAPTER shape the compare screen's QuoteCard wants —
   * which has no `id`, `status` or `quote_number`. The read-only customer
   * screen needs those, so this is the same call under the honest type rather
   * than a cast at the use site.
   */
  async getQuoteOptions(quoteRequestId: string): Promise<QuoteOption[]> {
    const response = await api.get<PaginatedResponse<QuoteOption>>(
      `/quotes/quotes/?quote_request=${encodeURIComponent(quoteRequestId)}`
    );
    return response.data.results;
  },

  async getQuote(id: string): Promise<Quote> {
    const response = await api.get<Quote>(`/quotes/requests/${id}/`);
    return response.data;
  },

  async createQuote(data: QuoteCreateRequest): Promise<Quote> {
    const response = await api.post<Quote>('/quotes/requests/', data);
    return response.data;
  },

  async updateQuote(id: string, data: Partial<QuoteCreateRequest>): Promise<Quote> {
    const response = await api.patch<Quote>(`/quotes/requests/${id}/`, data);
    return response.data;
  },

  async deleteQuote(id: string): Promise<void> {
    await api.delete(`/quotes/requests/${id}/`);
  },

  /** BROKER-7: move a quote through draft/submitted/processing/completed/
   *  expired/cancelled. */
  async updateQuoteStatus(id: string, status: string): Promise<Quote> {
    const response = await api.post<Quote>(`/quotes/requests/${id}/update_status/`, { status });
    return response.data;
  },

  /** BROKER-7: assign (or clear, with null) the POSP who owns this quote. */
  async assignQuotePosp(id: string, posp: string | null): Promise<Quote> {
    const response = await api.post<Quote>(`/quotes/requests/${id}/assign_posp/`, { posp });
    return response.data;
  },

  /** BROKER-7: mint (idempotently) the public link for sharing with a customer. */
  async shareQuote(id: string): Promise<{ public_token: string; public_url: string }> {
    const response = await api.post<{ public_token: string; public_url: string }>(
      `/quotes/requests/${id}/share/`
    );
    return response.data;
  },

  /**
   * Send the public link to the customer over WhatsApp or email.
   *
   * Publishes the link as a side effect, exactly as `shareQuote` does, and
   * sends through the Communication module so the broker's own channel,
   * provider and template apply. `public_url` here is ABSOLUTE (the server
   * builds it from SITE_URL) — unlike `shareQuote`, which returns a path.
   */
  async shareQuoteVia(
    id: string,
    channel: 'whatsapp' | 'email'
  ): Promise<{
    channel: string;
    recipient: string;
    public_url: string;
    used_template: boolean;
    message_id: string | null;
    status: string | null;
  }> {
    const response = await api.post(`/quotes/requests/${id}/share-via/`, { channel });
    return response.data;
  },

  async submitQuote(id: string): Promise<Quote> {
    const response = await api.post<Quote>(`/quotes/requests/${id}/submit/`);
    return response.data;
  },

  async cancelQuote(id: string): Promise<Quote> {
    const response = await api.post<Quote>(`/quotes/requests/${id}/cancel/`);
    return response.data;
  },

  /**
   * Attachments on a quote request.
   *
   * `quote_pdf` and `comparison_pdf` are the two types the customer sees on the
   * public link; every other QuoteDocument type (KYC, ID proof, address proof)
   * stays broker-only and is filtered out server-side.
   */
  async getQuoteDocuments(quoteRequestId: string): Promise<PaginatedResponse<QuoteDocument>> {
    const response = await api.get<PaginatedResponse<QuoteDocument>>('/quotes/documents/', {
      params: { quote_request: quoteRequestId },
    });
    return response.data;
  },

  async uploadQuoteDocument(formData: FormData): Promise<QuoteDocument> {
    const response = await api.post<QuoteDocument>('/quotes/documents/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteQuoteDocument(documentId: string): Promise<void> {
    await api.delete(`/quotes/documents/${documentId}/`);
  },

  /** D3: accept the customer's KYC. This is what opens the payment/issuance
   *  gate — the server refuses it from any status but `kyc_pending_approval`. */
  async approveQuoteKyc(id: string): Promise<Quote> {
    const response = await api.post<Quote>(`/quotes/requests/${id}/approve-kyc/`);
    return response.data;
  },

  /** D3: send the customer back for better documents. The reason is mandatory
   *  server-side — it is shown to them on their own public link. */
  async rejectQuoteKyc(id: string, reason: string): Promise<Quote> {
    const response = await api.post<Quote>(`/quotes/requests/${id}/reject-kyc/`, { reason });
    return response.data;
  },

  /** D4: the broker records the reference the customer paid against. Refused by
   *  the server unless the quote is `kyc_approved` — the button being hidden is
   *  presentation, the refusal is the permission. */
  async recordQuotePayment(
    id: string,
    payment: { payment_reference: string; payment_date: string; payment_mode: string }
  ): Promise<Quote> {
    const response = await api.post<Quote>(`/quotes/requests/${id}/record-payment/`, payment);
    return response.data;
  },

  /** Fetch a quote attachment as a blob so the browser saves it under its real
   *  name. Goes through `api` rather than a bare href because the endpoint is
   *  JWT-authenticated — a plain link would arrive unauthenticated and 401. */
  async downloadQuoteDocument(documentId: string): Promise<Blob> {
    const response = await api.get<Blob>(`/quotes/documents/${documentId}/download/`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
